import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserService } from '../store/settings/user/service/user.service';
import {
  AppsCollection,
  applyAppBodyClass,
  getAppToRouteTo,
  setAppToRouteTo,
} from './app-constants';
import { AppNav } from './app-enum';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private userService: UserService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.userService.user$.pipe(
      take(1),
      switchMap((loggedUser) => {
        const restoredUser = loggedUser?.uuid
          ? loggedUser
          : this.userService.restoreSession();

        if (restoredUser?.uuid && environment.token !== '') {
          // on a restored session /login is skipped, so run its server-side
          // post-login work (recurring expenses/income for a new month)
          // before the app starts fetching data
          let sessionSync$: Observable<unknown> = of(null);
          if (!loggedUser?.uuid) {
            this.userService.setUser(restoredUser);
            sessionSync$ = this.userService.SyncRestoredSession$();
          }

          return sessionSync$.pipe(
            map(() => {
              if (this.isRootUrl(state.url)) {
                const app = getAppToRouteTo();
                setAppToRouteTo(app);
                applyAppBodyClass(app);
                return this.router.parseUrl(app.route);
              }

              return true;
            }),
          );
        }

        // Queue the app the user was trying to reach so login returns them to it
        // (e.g. a direct link to a now-login-required game).
        const path = state.url.split('?')[0];
        const attempted = AppsCollection.find(
          (a) => path === a.route || path.startsWith(a.route + '/'),
        );
        if (attempted) setAppToRouteTo(attempted);

        this.userService.clearSession();
        return of(this.router.parseUrl(AppNav.login));
      }),
    );
  }

  private isRootUrl(url: string): boolean {
    const path = url.split('?')[0];
    return path === '' || path === '/';
  }
}
